import React, { useMemo, useState, useEffect, useRef } from "react";
import { View, Text, TextInput, Pressable, ScrollView, StyleSheet, ActivityIndicator, Animated, Platform } from "react-native";
import { router, usePathname } from 'expo-router';
import * as Sentry from '@sentry/react-native';
import { supabase } from '../lib/supabase';
import { activateDiscoveryProject, isAutoGeneratedProjectName } from '../lib/discoveryProjectService';
import type { Project, Category, ProjectKeyword } from '../lib/database.client.types';
import { isProjectActivated } from '../lib/projectActivation';
import colors from '../lib/colors';
import { fonts } from "../lib/fonts";
import { trackFunnelEvent } from '../lib/funnelEvents';
import { getCurrentJourneyId } from '../lib/funnelJourney';
import { getE2ETestRunContext } from '../lib/e2eTestRun';
import { buildDashboardPath } from '../lib/dashboardRoutes';
import {
    getDeliveryIdentityRequirement,
    normalizeDeliveryName,
    validateDeliveryIdentity,
} from '../lib/deliveryIdentity';
import {
    getDeliverySaveErrorMessage,
    isDeliverySaveStatementTimeout,
    retryDeliverySaveTimeout,
    toDeliverySaveError,
} from '../lib/deliverySaveRetry';
import {
    buildForwardedPreparingRoute,
    isForwardedOnboardingSource,
} from '../lib/forwardedOnboarding';
import VerificationCodePrompt from './VerificationCodePrompt';
import { useResendCooldown } from '../hooks/useResendCooldown';
import {
    buildPasswordlessCallbackUrl,
    markProjectPendingActivation,
    resendPasswordlessEmailCode,
    resolveProjectHandoffDestination,
    sendPasswordlessEmailCode,
} from '../lib/passwordlessEmail';
import { getAuthMessageLocaleMetadata } from '../lib/clientSessionMetadata';
import { normalizeEmailAddress } from '../utils/email';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import {
    type LocalizedMessage,
    useOptionalResolvedMessage,
    useResolvedMessage,
} from '../lib/i18n/messages';

interface ProjectWithRelations extends Project {
    project_category_interests?: Array<{
        category?: Category;
    }>;
    project_keywords?: ProjectKeyword[];
}

interface DeliveryContentProps {
    projectId: string;
    project: ProjectWithRelations | null;
    isLoading: boolean;
    isPreview?: boolean;
    sessionFirstName?: string | null;
    showCancelButton?: boolean;
    setupSource?: string | null;
}

type DeliveryFrequency = 'daily' | 'weekly' | 'pause';

const PILL_OPTIONS = [3, 5, 10, 15, 20];
const MIN = 3;
const MAX = 20;
const NEW_PROJECT_FREQUENCY_OPTIONS: Array<{
    value: Exclude<DeliveryFrequency, 'pause'>;
    label: MessageDescriptor;
}> = [
    {
        value: 'daily',
        label: msg({ id: 'delivery.frequency.daily', message: 'Daily' }),
    },
    {
        value: 'weekly',
        label: msg({ id: 'delivery.frequency.weekly', message: 'Weekly' }),
    },
];
const EXISTING_PROJECT_FREQUENCY_OPTIONS: Array<{
    value: DeliveryFrequency;
    label: MessageDescriptor;
}> = [
    ...NEW_PROJECT_FREQUENCY_OPTIONS,
    {
        value: 'pause',
        label: msg({ id: 'delivery.frequency.pause', message: 'Pause' }),
    },
];

function normalizeDeliveryFrequency(value?: string | null): DeliveryFrequency {
    if (value === 'weekly' || value === 'pause') {
        return value;
    }
    return 'daily';
}

function deliveryFrequencyMessage(value: DeliveryFrequency): MessageDescriptor {
    if (value === 'weekly') return msg({ id: 'delivery.frequency.weekly', message: 'Weekly' });
    if (value === 'pause') return msg({ id: 'delivery.frequency.paused', message: 'Paused' });
    return msg({ id: 'delivery.frequency.daily', message: 'Daily' });
}

function SectionLabel({ label }: { label: LocalizedMessage }) {
    const resolvedLabel = useResolvedMessage(label);
    return (
        <Text style={styles.sectionLabel}>{resolvedLabel}</Text>
    );
}

function Helper({ text }: { text: LocalizedMessage }) {
    const resolvedText = useResolvedMessage(text);
    return <Text style={styles.helper}>{resolvedText}</Text>;
}

function ErrorText({ text, fieldName, touchedFields, submitted }: {
    text: LocalizedMessage | null | undefined;
    fieldName: string;
    touchedFields: Record<string, boolean>;
    submitted: boolean;
}) {
    const resolvedText = useOptionalResolvedMessage(text);
    if (!text || (!touchedFields[fieldName] && !submitted)) return null;
    return <Text style={styles.errorText}>{resolvedText}</Text>;
}

function Pill({ value, selected, onPress, disabled }: { value: number; selected: boolean; onPress: () => void; disabled?: boolean }) {
    return (
        <Pressable
            onPress={disabled ? undefined : onPress}
            style={({ pressed }) => [
                styles.pill,
                selected && styles.pillSelected,
                pressed && !selected && !disabled && styles.pillPressed,
                disabled && styles.pillDisabled
            ]}
        >
            <Text style={[styles.pillText, selected && styles.pillTextSelected, disabled && styles.pillTextDisabled]}>{value}</Text>
        </Pressable>
    );
}

function FrequencyOption({
    label,
    selected,
    onPress,
    disabled,
    testID,
}: {
    label: LocalizedMessage;
    selected: boolean;
    onPress: () => void;
    disabled?: boolean;
    testID?: string;
}) {
    const resolvedLabel = useResolvedMessage(label);
    return (
        <Pressable
            onPress={disabled ? undefined : onPress}
            style={({ pressed }) => [
                styles.frequencyOption,
                selected && styles.frequencyOptionSelected,
                pressed && !selected && !disabled && styles.frequencyOptionPressed,
                disabled && styles.frequencyOptionDisabled,
            ]}
            testID={testID}
        >
            <Text style={[
                styles.frequencyOptionLabel,
                selected && styles.frequencyOptionLabelSelected,
                disabled && styles.frequencyOptionLabelDisabled,
            ]}>
                {resolvedLabel}
            </Text>
        </Pressable>
    );
}

function Stepper({ value, setValue, disabled }: { value: number; setValue: (n: number) => void; disabled?: boolean }) {
    const dec = () => {
        if (!disabled) {
            setValue(Math.max(MIN, value - 1));
        }
    };
    const inc = () => {
        if (!disabled) {
            setValue(Math.min(MAX, value + 1));
        }
    };
    return (
        <View style={styles.stepperContainer}>
            <Pressable onPress={disabled ? undefined : dec} style={({ pressed }) => [styles.stepperButton, pressed && !disabled && styles.stepperButtonPressed, disabled && styles.stepperButtonDisabled]}>
                <Text style={[styles.stepperButtonText, disabled && styles.stepperButtonTextDisabled]}>–</Text>
            </Pressable>
            <View style={styles.stepperValue}>
                <Text style={styles.stepperValueText}>{value}</Text>
            </View>
            <Pressable onPress={disabled ? undefined : inc} style={({ pressed }) => [styles.stepperButton, pressed && !disabled && styles.stepperButtonPressed, disabled && styles.stepperButtonDisabled]}>
                <Text style={[styles.stepperButtonText, disabled && styles.stepperButtonTextDisabled]}>+</Text>
            </Pressable>
        </View>
    );
}

function PrimaryButton({ title, onPress, loading, disabled, testID }: {
    title: LocalizedMessage;
    onPress?: () => void;
    loading?: boolean;
    disabled?: boolean;
    testID?: string;
}) {
    const resolvedTitle = useResolvedMessage(title);
    return (
        <Pressable
            onPress={disabled || loading ? undefined : onPress}
            style={({ pressed }) => [
                styles.primaryButton,
                pressed && !disabled && !loading && styles.primaryButtonPressed,
                (disabled || loading) && styles.primaryButtonDisabled
            ]}
            testID={testID}
        >
            <View style={styles.primaryButtonContent}>
                {loading && <ActivityIndicator size="small" color="white" style={styles.buttonLoader} />}
                <Text style={[
                    styles.primaryButtonText,
                    (disabled || loading) && styles.primaryButtonTextDisabled
                ]}>{resolvedTitle}</Text>
            </View>
        </Pressable>
    );
}

function SecondaryButton({ title, onPress }: { title: LocalizedMessage; onPress?: () => void }) {
    const resolvedTitle = useResolvedMessage(title);
    return (
        <Pressable onPress={onPress} style={({ pressed }) => [styles.secondaryButton, pressed && styles.secondaryButtonPressed]}>
            <Text style={styles.secondaryButtonText}>{resolvedTitle}</Text>
        </Pressable>
    );
}

export default function DeliveryContent({
    projectId,
    project,
    isLoading,
    isPreview = false,
    sessionFirstName = null,
    showCancelButton = false,
    setupSource = null,
}: DeliveryContentProps) {
    const pathname = usePathname();
    const [projectName, setProjectName] = useState("");
    const [maxPapers, setMaxPapers] = useState<number>(10);
    const [receiverName, setReceiverName] = useState("");
    const [receiverEmail, setReceiverEmail] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<LocalizedMessage | null>(null);
    const resolvedSaveError = useOptionalResolvedMessage(saveError);
    const [saveNotice, setSaveNotice] = useState<LocalizedMessage | null>(null);
    const resolvedSaveNotice = useOptionalResolvedMessage(saveNotice);
    const [conversionSuccess, setConversionSuccess] = useState(false);
    const [conversionMode, setConversionMode] = useState<'verify_new' | 'signin_existing' | null>(null);
    const [verificationResendLoading, setVerificationResendLoading] = useState(false);
    const [verificationResendError, setVerificationResendError] = useState<LocalizedMessage | null>(null);
    const [emailFrequency, setEmailFrequency] = useState<DeliveryFrequency>('daily');
    const resolvedDeliveryFrequency = useResolvedMessage(deliveryFrequencyMessage(emailFrequency));
    const resolvedEmailPlaceholder = useResolvedMessage(msg({
        id: 'delivery.placeholder.email',
        message: 'first.last@university.edu',
    }));
    const [hasInitialized, setHasInitialized] = useState(false);
    const {
        resendCooldown: verificationResendCooldown,
        startResendCooldown: startVerificationResendCooldown,
    } = useResendCooldown();

    // Mount animation for card
    const fadeAnim = useRef(new Animated.Value(0)).current;
    const translateY = useRef(new Animated.Value(8)).current;

    useEffect(() => {
        Animated.parallel([
            Animated.timing(fadeAnim, {
                toValue: 1,
                duration: 400,
                useNativeDriver: Platform.OS !== 'web',
            }),
            Animated.timing(translateY, {
                toValue: 0,
                duration: 400,
                useNativeDriver: Platform.OS !== 'web',
            }),
        ]).start();

        const processingState = project?.processing_phase && project.processing_phase !== 'completed'
            ? 'active'
            : 'completed';

        trackFunnelEvent('delivery_opened', {
            project_id: projectId,
            user_type: isPreview ? 'anonymous' : 'authenticated',
            processing_state: processingState,
        });
    }, [fadeAnim, translateY, projectId, isPreview, project?.processing_phase]);

    const isExistingProject = isProjectActivated(project);
    const frequencyOptions = isExistingProject
        ? EXISTING_PROJECT_FREQUENCY_OPTIONS
        : NEW_PROJECT_FREQUENCY_OPTIONS;
    const identityRequirement = useMemo(() => getDeliveryIdentityRequirement({
        isPreview,
        sessionFirstName,
    }), [isPreview, sessionFirstName]);

    // Form should be disabled for existing projects while loading OR while saving
    const isFormDisabled = (isExistingProject && isLoading) || isSaving;

    // Show max papers setting only for active projects (editing mode)
    // First-time setup (experimental) uses DB default of 10
    const showMaxPapers = project?.mode === 'active';

    // Helper function to mark fields as touched
    const markFieldAsTouched = (fieldName: string) => {
        setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    };

    const projectProcessingPhase = project?.processing_phase || null;
    const isProcessingPending = projectProcessingPhase !== null && projectProcessingPhase !== 'completed';
    const projectOverviewPath = buildDashboardPath(pathname, `/${projectId}`);
    const projectPreparingPath = buildDashboardPath(pathname, `/${projectId}/settings?onboarding=preparing`);
    const projectSettingsPath = buildDashboardPath(pathname, `/${projectId}/settings`);
    const isForwardedSetup = isForwardedOnboardingSource(setupSource);
    const postSaveDestination = isForwardedSetup
        ? buildForwardedPreparingRoute(pathname, projectId)
        : isProcessingPending
            ? projectPreparingPath
            : showCancelButton
                ? projectSettingsPath
                : projectOverviewPath;
    const journeyId = getCurrentJourneyId() || undefined;
    const deliveryFunnelContext = {
        source: 'form' as const,
        step: 'delivery' as const,
        projectId,
        journeyId,
        processingState: isProcessingPending ? 'active' as const : 'completed' as const,
    };
    const authCallbackBaseUrl = Platform.OS === 'web' && typeof window !== 'undefined'
        ? `${window.location.origin}/auth`
        : `${process.env.EXPO_PUBLIC_BASE_URL || 'https://paperzilla.ai'}/auth`;

    // Initialize form with project data for existing projects, defaults for new projects
    // Only initialize once to prevent race conditions and real-time updates from clearing user input
    useEffect(() => {
        if (project && !hasInitialized) {
            setHasInitialized(true);
            // Only prefill if the user explicitly set a name (not auto-generated)
            if (project.name && !isAutoGeneratedProjectName(project.name)) {
                setProjectName(project.name);
            }
            setMaxPapers(project.max_papers_per_digests || 10);
            setEmailFrequency(normalizeDeliveryFrequency(project.email_frequency));
        }
    }, [project, hasInitialized]);

    const errors = useMemo(() => {
        const e: Record<string, string> = {};
        if (!projectName.trim()) e.projectName = "This field is required.";
        else if (projectName.trim().length < 3) e.projectName = "Enter at least 3 characters.";

        // Only validate max papers for active projects (when field is shown)
        if (project?.mode === 'active' && (Number.isNaN(maxPapers) || maxPapers < MIN || maxPapers > MAX)) {
            e.maxPapers = `Enter a number between ${MIN} and ${MAX}.`;
        }

        Object.assign(e, validateDeliveryIdentity({
            ...identityRequirement,
            name: receiverName,
            email: receiverEmail,
        }));

        return e;
    }, [projectName, maxPapers, receiverName, receiverEmail, identityRequirement, project?.mode]);

    const handleVerificationConfirmed = async () => {
        if (conversionMode === 'signin_existing') {
            const destination = await resolveProjectHandoffDestination(
                'email',
                postSaveDestination,
                deliveryFunnelContext
            );
            router.replace(destination);
            return;
        }

        await markProjectPendingActivation(projectId);
        router.replace(postSaveDestination);
    };

    const handleResendVerificationCode = async () => {
        if (verificationResendLoading || verificationResendCooldown > 0) {
            return;
        }

        const normalizedReceiverEmail = normalizeEmailAddress(receiverEmail);
        if (!normalizedReceiverEmail) {
            return;
        }

        setVerificationResendLoading(true);
        setVerificationResendError(null);

        const resendError = conversionMode === 'signin_existing'
            ? await sendPasswordlessEmailCode({
                email: normalizedReceiverEmail,
                redirectTo: postSaveDestination,
                funnelContext: deliveryFunnelContext,
                callbackBaseUrl: authCallbackBaseUrl,
            })
            : await resendPasswordlessEmailCode({
                email: normalizedReceiverEmail,
                otpType: 'email_change',
                redirectTo: postSaveDestination,
                funnelContext: deliveryFunnelContext,
                callbackBaseUrl: authCallbackBaseUrl,
            });

        setVerificationResendLoading(false);

        if (resendError) {
            console.error('[DeliveryContent] Could not resend verification code:', resendError);
            setVerificationResendError(msg({ id: 'delivery.verificationResendFailure', message: 'Failed to resend verification code' }));
            return;
        }

        startVerificationResendCooldown(60);
    };

    const onSave = async () => {
        setSubmitted(true);
        setSaveError(null);
        setSaveNotice(null);

        trackFunnelEvent('delivery_save_attempted', {
            project_id: projectId,
            user_type: isPreview ? 'anonymous' : 'authenticated',
            has_errors: Object.keys(errors).length > 0,
            error_fields: Object.keys(errors),
        });

        if (Object.keys(errors).length === 0) {
            setIsSaving(true);
            let saveStage = 'project_settings_update';
            let saveTimeoutRetryCount = 0;

            const runRetryableSaveOperation = async <T,>(
                stage: string,
                operation: (attempt: number) => Promise<T>
            ): Promise<T> => {
                saveStage = stage;

                return retryDeliverySaveTimeout(operation, {
                    onRetry: ({ attempt, maxAttempts, delayMs, error }) => {
                        saveTimeoutRetryCount += 1;
                        console.warn('⚠️ [DeliveryContent] Save timed out; retrying:', {
                            stage,
                            attempt,
                            maxAttempts,
                            retryInMs: delayMs,
                            message: error.message,
                        });
                        setSaveNotice(msg({ id: 'delivery.savingSlowly', message: 'Saving is taking longer than usual, so we are trying again.' }));
                    },
                });
            };

            try {
                // Update project with settings
                console.log('💾 [DeliveryContent] Updating project with settings:', {
                    projectId,
                    name: projectName,
                    emailFrequency,
                    maxPapers,
                    receiverName,
                    receiverEmail,
                    isPreview
                });

                await runRetryableSaveOperation('project_settings_update', async () => {
                    const { error: updateError } = await supabase
                        .from('project')
                        .update({
                            name: projectName.trim(),
                            email_frequency: emailFrequency,
                            // Only update max_papers when editing active projects (field is shown)
                            // First-time setup (experimental) uses DB default of 10
                            ...(project?.mode === 'active' && { max_papers_per_digests: maxPapers }),
                        })
                        .eq('id', projectId)
                        .is('deleted_at', null)
                        .select('id')
                        .single();

                    if (updateError) {
                        throw updateError;
                    }
                });

                console.log('✅ [DeliveryContent] Project updated successfully');

                // Step 2: Handle based on user type
                const { data: { session } } = await supabase.auth.getSession();

                if (session && !session.user.email) {
                    // Anonymous user - convert to permanent
                    console.log('🔄 [DeliveryContent] Converting anonymous user to authenticated...');
                    const normalizedReceiverEmail = normalizeEmailAddress(receiverEmail);
                    const e2eContext = getE2ETestRunContext();
                    const { error } = await supabase.auth.updateUser({
                        email: normalizedReceiverEmail,
                        data: {
                            ...getAuthMessageLocaleMetadata(),
                            first_name: receiverName,
                            project_id: projectId,
                            conversion_type: 'digest',
                            ...(e2eContext
                                ? {
                                    test_run: true,
                                    test_run_id: e2eContext.runId,
                                    test_run_tag: e2eContext.tag,
                                }
                                : {}),
                        }
                    }, {
                        emailRedirectTo: buildPasswordlessCallbackUrl({
                            otpType: 'email_change',
                            redirectTo: postSaveDestination,
                            funnelContext: deliveryFunnelContext,
                            callbackBaseUrl: authCallbackBaseUrl,
                        })
                    });

                    if (error) {
                        console.error('❌ [DeliveryContent] Failed to convert user:', error);
                        if (error.message?.includes('already been registered')) {
                            try {
                                const { data: handoffRequestData, error: handoffRequestError } = await supabase.rpc(
                                    'request_project_handoff' as any,
                                    {
                                        p_project_id: projectId,
                                        p_target_email: normalizedReceiverEmail,
                                        p_fallback_redirect: postSaveDestination,
                                    } as any
                                );

                                if (handoffRequestError) {
                                    console.warn('⚠️ [DeliveryContent] Could not create project handoff request:', handoffRequestError);
                                } else {
                                    const handoffStatus = (handoffRequestData as { status?: string } | null)?.status;
                                    console.log('🔁 [DeliveryContent] Project handoff request status:', handoffStatus || 'unknown');
                                }
                            } catch (handoffRequestException) {
                                console.warn('⚠️ [DeliveryContent] Project handoff request failed, falling back to resume flow:', handoffRequestException);
                            }

                            const handoffError = await sendPasswordlessEmailCode({
                                email: normalizedReceiverEmail,
                                redirectTo: postSaveDestination,
                                funnelContext: deliveryFunnelContext,
                                callbackBaseUrl: authCallbackBaseUrl,
                            });

                            if (handoffError) {
                                throw new Error(handoffError.message || 'Failed to send sign-in email');
                            }

                            trackFunnelEvent('delivery_save_outcome', {
                                project_id: projectId,
                                user_type: 'anonymous',
                                outcome: 'existing_account_handoff',
                                processing_state: isProcessingPending ? 'active' : 'completed',
                            });

                            setReceiverEmail(normalizedReceiverEmail);
                            setVerificationResendError(null);
                            startVerificationResendCooldown(60);
                            setConversionMode('signin_existing');
                            setConversionSuccess(true);
                            return;
                        }
                        throw new Error(error.message || 'Failed to send verification email');
                    }

                    console.log('✅ [DeliveryContent] User conversion initiated, verification email sent');
                    trackFunnelEvent('delivery_save_outcome', {
                        project_id: projectId,
                        user_type: 'anonymous',
                        outcome: 'verification_sent',
                        processing_state: isProcessingPending ? 'active' : 'completed',
                    });

                    // Show success confirmation screen
                    setReceiverEmail(normalizedReceiverEmail);
                    setVerificationResendError(null);
                    startVerificationResendCooldown(60);
                    setConversionMode('verify_new');
                    setConversionSuccess(true);
                } else if (session && session.user.email) {
                    // Authenticated user - activate project and redirect to project view
                    console.log('✅ [DeliveryContent] Project settings saved for authenticated user');
                    const currentIdentityRequirement = getDeliveryIdentityRequirement({
                        isPreview: false,
                        sessionFirstName: session.user.user_metadata?.first_name,
                    });

                    if (currentIdentityRequirement.requiresName) {
                        const normalizedName = normalizeDeliveryName(receiverName);
                        const { error: updateUserError } = await supabase.auth.updateUser({
                            data: {
                                first_name: normalizedName,
                            }
                        });

                        if (updateUserError) {
                            throw new Error(updateUserError.message || 'Failed to save your name');
                        }

                        const { error: profileError } = await supabase
                            .from('profiles')
                            .update({ first_name: normalizedName })
                            .eq('id', session.user.id);

                        if (profileError) {
                            console.warn('⚠️ [DeliveryContent] Failed to sync profile first name:', profileError);
                        }
                    }

                    // Activate project for digest delivery
                    console.log('🚀 [DeliveryContent] Activating project for digest delivery...');
                    await runRetryableSaveOperation('project_activation', async () => {
                        await activateDiscoveryProject(projectId);
                    });
                    console.log('✅ [DeliveryContent] Project activated (mode=active)');
                    trackFunnelEvent('delivery_save_outcome', {
                        project_id: projectId,
                        user_type: 'authenticated',
                        outcome: 'activated',
                        processing_state: isProcessingPending ? 'active' : 'completed',
                    });

                    console.log('💾 [DeliveryContent] Redirecting to project view:', {
                        name: projectName,
                        maxPapers,
                        projectId
                    });

                    router.replace(postSaveDestination);
                } else {
                    // No session - this shouldn't happen
                    console.warn('⚠️ [DeliveryContent] No session found');
                    throw new Error('No session found. Please refresh the page and try again.');
                }

            } catch (error: any) {
                const normalizedError = toDeliverySaveError(error);
                const isStatementTimeout = isDeliverySaveStatementTimeout(normalizedError);

                console.error('❌ [DeliveryContent] Save error:', normalizedError);
                setSaveError(getDeliverySaveErrorMessage(normalizedError));
                Sentry.captureException(normalizedError, {
                    tags: {
                        feature: 'delivery',
                        action: 'save_project',
                        user_type: isPreview ? 'anonymous' : 'authenticated',
                        statement_timeout: isStatementTimeout ? 'true' : 'false',
                        retry_exhausted: isStatementTimeout && saveTimeoutRetryCount > 0 ? 'true' : 'false',
                    },
                    extra: {
                        project_id: projectId,
                        project_mode: project?.mode || null,
                        save_stage: saveStage,
                        timeout_retry_count: saveTimeoutRetryCount,
                        processing_state: isProcessingPending ? 'active' : 'completed',
                        setup_source: setupSource || null,
                    },
                });
                trackFunnelEvent('delivery_save_outcome', {
                    project_id: projectId,
                    user_type: isPreview ? 'anonymous' : 'authenticated',
                    outcome: 'failed',
                    processing_state: isProcessingPending ? 'active' : 'completed',
                });
            } finally {
                setSaveNotice(null);
                setIsSaving(false);
            }
        }
    };

    const inputStyle = (fieldName: string, hasError?: string) => [
        styles.textInput,
        hasError && (touchedFields[fieldName] || submitted) && styles.textInputError
    ];

    // Show confirmation screen after successful conversion
    if (conversionSuccess) {
        const isExistingAccountHandoff = conversionMode === 'signin_existing';

        return (
            <View style={styles.container} testID="delivery-conversion-confirmation">
                <View style={styles.confirmationContainer}>
                    <View style={styles.confirmationCard}>
                        <Text style={styles.confirmationTitle}>
                            {isExistingAccountHandoff ? <Trans id="deliverycontent.signInToContinueSetup" comment="Conditional UI copy in components/DeliveryContent.tsx">Sign in to continue setup</Trans> : <Trans id="deliverycontent.projectCreatedSuccessfully" comment="Conditional UI copy in components/DeliveryContent.tsx">Project Created Successfully!</Trans>}
                        </Text>
                        <Text style={styles.confirmationSubtitle}>
                            {isExistingAccountHandoff
                                ? <Trans id="deliverycontent.weSentAVerificationCodeToYourAccount" comment="Conditional UI copy in components/DeliveryContent.tsx">We sent a verification code to your account</Trans>
                                : <Trans id="deliverycontent.enterTheCodeFromYourEmailToActivateYourProject" comment="Conditional UI copy in components/DeliveryContent.tsx">Enter the code from your email to activate your project</Trans>}
                        </Text>

                        <View style={styles.confirmationDetails}>
                            <VerificationCodePrompt
                                email={normalizeEmailAddress(receiverEmail)}
                                emailLabel="Verification code sent to:"
                                instructionText={isExistingAccountHandoff
                                    ? 'Enter the latest 6-digit code from your email to sign in and continue setup.'
                                    : 'Enter the latest 6-digit code from your email to activate your project.'}
                                otpType={isExistingAccountHandoff ? 'email' : 'email_change'}
                                onVerified={handleVerificationConfirmed}
                                resendCooldown={verificationResendCooldown}
                                resendLoading={verificationResendLoading}
                                resendError={verificationResendError}
                                onResend={handleResendVerificationCode}
                            />

                            <View style={styles.digestSummary}>
                                <Text style={styles.confirmationSummaryTitle}><Trans id="deliverycontent.yourProjectDetails" comment="UI copy in components/DeliveryContent.tsx">Your project details:</Trans></Text>
                                <Text style={styles.confirmationSummaryItem}><Trans id="deliverycontent.name" comment="UI copy in components/DeliveryContent.tsx">• Name: {projectName}</Trans></Text>
                                <Text style={styles.confirmationSummaryItem}><Trans id="deliverycontent.delivery" comment="UI copy in components/DeliveryContent.tsx">• Delivery: {resolvedDeliveryFrequency}</Trans></Text>
                                <Text style={styles.confirmationSummaryItem}><Trans id="deliverycontent.maxPapersPerEmail" comment="UI copy in components/DeliveryContent.tsx">• Max papers per email: {maxPapers}</Trans></Text>
                                <Text style={styles.confirmationSummaryItem}><Trans id="deliverycontent.receiver" comment="UI copy in components/DeliveryContent.tsx">• Receiver: {receiverName}</Trans></Text>
                            </View>
                        </View>
                    </View>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container} testID="delivery-content">
            {/* Top header */}
            <View style={styles.header}>
                <Text style={styles.title}><Trans id="deliverycontent.saveYourProject" comment="UI copy in components/DeliveryContent.tsx">Save your project</Trans></Text>
            </View>

            {/* {isPreview && (
                <View style={styles.previewBanner}>
                    <Text style={styles.previewText}>
                        🌟 Sign up to enable delivery options and save your digest!
                    </Text>
                </View>
            )} 

            // STYLES:
                previewBanner: {
                backgroundColor: colors.veryLightGreen,
                borderColor: colors.primaryGreen,
                borderWidth: 1,
                borderRadius: 12,
                padding: 12,
                marginTop: 12,
                marginBottom: 16,
            },
            previewText: {
                color: colors.primaryGreenDark,
                fontWeight: "700",
                textAlign: "center",
                marginBottom: 4,
            },
             
             */}

            {/* Error summary */}
            {resolvedSaveNotice && !saveError && (
                <View style={styles.saveNotice}>
                    <Text style={styles.saveNoticeTitle}><Trans id="deliverycontent.stillSaving" comment="UI copy in components/DeliveryContent.tsx">Still saving</Trans></Text>
                    <Text style={styles.saveNoticeItem}>{resolvedSaveNotice}</Text>
                </View>
            )}

            {((submitted && Object.keys(errors).length > 0) || resolvedSaveError) && (
                <View style={styles.errorSummary}>
                    {resolvedSaveError && (
                        <>
                            <Text style={styles.errorSummaryTitle}><Trans id="deliverycontent.weCouldNotSaveThisYet" comment="UI copy in components/DeliveryContent.tsx">We could not save this yet</Trans></Text>
                            <Text style={styles.errorSummaryItem}>{resolvedSaveError}</Text>
                        </>
                    )}
                    {submitted && Object.keys(errors).length > 0 && (
                        <>
                            <Text style={styles.errorSummaryTitle}><Trans id="deliverycontent.pleaseFixFieldS" comment="UI copy in components/DeliveryContent.tsx">Please fix {Object.keys(errors).length} field(s)</Trans></Text>
                            {Object.entries(errors).map(([k, v]) => (
                                <Text key={k} style={styles.errorSummaryItem}>• {v}</Text>
                            ))}
                        </>
                    )}
                </View>
            )}

            {/* Content */}
            <ScrollView contentContainerStyle={styles.scrollContent}>
                <View style={styles.formContainer}>
                    {/* Card */}
                        <Animated.View style={[
                            styles.card,
                            {
                                opacity: fadeAnim,
                                transform: [{ translateY }],
                        }
                        ]}>
                            {/* Project name */}
                        <View style={styles.fieldContainer}>
                            <SectionLabel label={msg({ id: 'delivery.field.projectName', message: 'Project name' })} />
                            <TextInput
                                value={projectName}
                                onChangeText={setProjectName}
                                onBlur={() => markFieldAsTouched('projectName')}
                                style={inputStyle('projectName', errors.projectName)}
                                editable={!isFormDisabled}
                                testID="delivery-project-name-input"
                            />
                            <Helper text={msg({ id: 'delivery.helper.projectName', message: "A short, descriptive title. You'll see this in your dashboard and email digests." })} />
                            <ErrorText text={errors.projectName} fieldName="projectName" touchedFields={touchedFields} submitted={submitted} />
                        </View>

                        <View style={styles.fieldContainer}>
                            <SectionLabel label={msg({ id: 'delivery.field.emailFrequency', message: 'Email frequency' })} />
                            <View style={styles.frequencyOptions}>
                                {frequencyOptions.map((option) => (
                                    <FrequencyOption
                                        key={option.value}
                                        label={option.label}
                                        selected={emailFrequency === option.value}
                                        onPress={() => setEmailFrequency(option.value)}
                                        disabled={isFormDisabled}
                                        testID={`delivery-frequency-option-${option.value}`}
                                    />
                                ))}
                            </View>
                            <Helper
                                text={isExistingProject
                                    ? msg({
                                        id: 'delivery.helper.existingFrequency',
                                        message: 'Choose daily, weekly, or pause email delivery. Your feed and discovery continue even when emails are paused.',
                                    })
                                    : msg({
                                        id: 'delivery.helper.newFrequency',
                                        message: 'Choose whether we should send papers daily or bundle them into a weekly digest. You can change this later.',
                                    })}
                            />
                        </View>

                        {/* Max papers - only shown for active projects (editing mode) */}
                        {showMaxPapers && (
                            <View style={styles.fieldContainer}>
                                <SectionLabel label={msg({ id: 'delivery.field.maxPapers', message: 'Max papers per email' })} />
                                <View style={styles.maxPapersContainer}>
                                    <View style={styles.pillsContainer}>
                                        {PILL_OPTIONS.map((n) => (
                                            <Pill
                                                key={n}
                                                value={n}
                                                selected={n === maxPapers}
                                                onPress={() => {
                                                    if (!isFormDisabled) {
                                                        setMaxPapers(n);
                                                        markFieldAsTouched('maxPapers');
                                                    }
                                                }}
                                                disabled={isFormDisabled}
                                            />
                                        ))}
                                    </View>
                                    <Stepper
                                        value={maxPapers}
                                        setValue={(value) => {
                                            setMaxPapers(value);
                                            markFieldAsTouched('maxPapers');
                                        }}
                                        disabled={isFormDisabled}
                                    />
                                </View>
                                <Helper text={msg({ id: 'delivery.helper.maxPapers', message: "We'll include up to this many relevant papers per digest (some days it may be fewer)." })} />
                                <ErrorText text={errors.maxPapers} fieldName="maxPapers" touchedFields={touchedFields} submitted={submitted} />
                            </View>
                        )}

                        {/* Receiver/name identity fields */}
                        {identityRequirement.requiresName && (
                            <View style={identityRequirement.requiresEmail ? styles.fieldContainer : styles.fieldContainerLast}>
                                <SectionLabel label={msg({ id: 'delivery.field.yourName', message: 'Your name' })} />
                                <TextInput
                                    value={receiverName}
                                    onChangeText={setReceiverName}
                                    onBlur={() => markFieldAsTouched('receiverName')}                                    
                                    style={inputStyle('receiverName', errors.receiverName)}
                                    editable={!isFormDisabled}
                                    testID={isPreview ? 'delivery-receiver-name-input' : 'delivery-authenticated-name-input'}
                                />
                                <Helper text={isPreview
                                    ? msg({ id: 'delivery.helper.previewName', message: 'Used in the greeting.' })
                                    : msg({ id: 'delivery.helper.accountName', message: 'Used to personalize your Paperzilla account and emails.' })} />
                                <ErrorText text={errors.receiverName} fieldName="receiverName" touchedFields={touchedFields} submitted={submitted} />
                            </View>
                        )}

                        {/* Receiver email - only for anonymous users */}
                        {identityRequirement.requiresEmail && (
                            <View style={styles.fieldContainerLast}>
                                <SectionLabel label={msg({ id: 'delivery.field.yourEmail', message: 'Your email' })} />
                                <TextInput
                                    value={receiverEmail}
                                    onChangeText={setReceiverEmail}
                                    onBlur={() => markFieldAsTouched('receiverEmail')}
                                    placeholder={resolvedEmailPlaceholder}
                                    placeholderTextColor={colors.mediumGray}
                                    keyboardType="email-address"
                                    autoCapitalize="none"
                                    style={inputStyle('receiverEmail', errors.receiverEmail)}
                                    editable={!isFormDisabled}
                                    testID="delivery-receiver-email-input"
                                />
                                <Helper text={msg({ id: 'delivery.helper.email', message: "We'll send your digests here." })} />
                                <ErrorText text={errors.receiverEmail} fieldName="receiverEmail" touchedFields={touchedFields} submitted={submitted} />
                            </View>
                        )}

                        {/* Add last class to max papers field when not showing receiver fields */}
                        {!isPreview && <View style={{ marginBottom: 6 }} />}
                    </Animated.View>

                    {/* Footer actions */}
                    <View style={styles.footerActions}>
                        <View style={styles.primaryActions}>
                            {showCancelButton && <SecondaryButton title={msg({ id: 'common.cancel', message: 'Cancel' })} onPress={() => {
                                router.push(projectSettingsPath);
                            }} />}
                            <PrimaryButton
                                title={isSaving
                                    ? msg({ id: 'delivery.saving', message: 'Saving...' })
                                    : msg({ id: 'delivery.saveProject', message: 'Save project' })}
                                onPress={onSave}
                                loading={isSaving}
                                disabled={isSaving}
                                testID="delivery-save-button"
                            />
                        </View>
                         {/* <View style={styles.secondaryActions}>
                            <LinkButton title="Preview digest" onPress={() => alert("Preview sample email…")} />
                            <LinkButton title="Save as draft" onPress={() => alert("Saved as draft")} />
                        </View> */}
                    </View>

                </View>
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create({
    // ============================================
    // CONTAINER & LAYOUT
    // ============================================
    container: {
        flex: 1,
        backgroundColor: colors.bgWeak,
        padding: 24,
        paddingTop: 32,
        paddingBottom: 48,
    },
    header: {
        paddingBottom: 28,
    },
    title: {
        ...fonts.h1,
        color: colors.text900,
        letterSpacing: -0.5,
    },
    scrollContent: {
        alignItems: "center",
        paddingBottom: 120,
    },
    formContainer: {
        width: "100%",
    },

    // ============================================
    // ERROR SUMMARY
    // ============================================
    errorSummary: {
        backgroundColor: "#fef2f2",
        borderRadius: 12,
        padding: 16,
        marginBottom: 20,
    },
    errorSummaryTitle: {
        ...fonts.bodySmall,
        color: colors.errorRed,
        fontWeight: "600",
        marginBottom: 6,
    },
    errorSummaryItem: {
        ...fonts.bodySmall,
        color: colors.errorRed,
    },
    saveNotice: {
        backgroundColor: colors.blue50,
        borderColor: colors.blue100,
        borderWidth: 1,
        borderRadius: 12,
        padding: 16,
        marginBottom: 20,
    },
    saveNoticeTitle: {
        ...fonts.bodySmall,
        color: colors.blue900,
        fontWeight: "600",
        marginBottom: 6,
    },
    saveNoticeItem: {
        ...fonts.bodySmall,
        color: colors.blue900,
    },

    // ============================================
    // CARD
    // ============================================
    card: {
        backgroundColor: colors.card,
        borderRadius: 16,
        padding: 28,
        ...(Platform.OS !== 'web' && {
            shadowColor: "#000",
            shadowOpacity: 0.04,
            shadowRadius: 24,
            shadowOffset: { width: 0, height: 4 },
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0px 4px 24px rgba(0,0,0,0.04)',
        }),
    },

    // ============================================
    // FORM FIELDS
    // ============================================
    fieldContainer: {
        marginBottom: 28,
    },
    fieldContainerLast: {
        marginBottom: 8,
    },
    sectionLabel: {
        ...fonts.bodyMedium,
        fontWeight: "600",
        color: colors.text900,
        marginBottom: 4,
    },
    textInput: {
        backgroundColor: colors.card,
        borderColor: colors.border,
        borderWidth: 1,
        borderRadius: 10,
        paddingVertical: 14,
        paddingHorizontal: 16,
        fontSize: 16,
        color: colors.text900,
    },
    textInputError: {
        borderColor: colors.errorRed,
    },
    helper: {
        ...fonts.bodySmall,
        color: colors.text600,
        marginTop: 8,
    },
    frequencyOptions: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
    },
    frequencyOption: {
        flexGrow: 0,
        flexBasis: 84,
        minWidth: 84,
        maxWidth: 84,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.card,
        paddingVertical: 8,
        paddingHorizontal: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    frequencyOptionSelected: {
        borderColor: '#c3d3bc',
        backgroundColor: '#f4f8f0',
    },
    frequencyOptionPressed: {
        backgroundColor: colors.bgWeak,
    },
    frequencyOptionDisabled: {
        opacity: 0.5,
    },
    frequencyOptionLabel: {
        ...fonts.bodySmall,
        color: colors.text900,
        fontWeight: '600',
    },
    frequencyOptionLabelSelected: {
        color: colors.primaryGreenDark,
    },
    frequencyOptionLabelDisabled: {
        color: colors.mediumGray,
    },
    errorText: {
        ...fonts.bodySmall,
        color: colors.errorRed,
        marginTop: 8,
    },

    // ============================================
    // MAX PAPERS SELECTOR
    // ============================================
    maxPapersContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
    },
    pillsContainer: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
        marginBottom: 8,
    },
    pill: {
        paddingVertical: 8,
        paddingHorizontal: 14,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.card,
    },
    pillSelected: {
        borderColor: '#c3d3bc',
        backgroundColor: '#f4f8f0',
    },
    pillPressed: {
        backgroundColor: colors.bgWeak,
    },
    pillText: {
        ...fonts.bodySmall,
        fontWeight: "500",
        color: colors.text900,
    },
    pillTextSelected: {
        color: colors.primaryGreenDark,
        fontWeight: "600",
    },

    // ============================================
    // STEPPER
    // ============================================
    stepperContainer: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
    },
    stepperButton: {
        width: 38,
        height: 38,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 14,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.card,
    },
    stepperButtonPressed: {
        backgroundColor: colors.bgWeak,
    },
    stepperButtonText: {
        fontSize: 18,
        color: colors.text700,
        fontWeight: "500",
    },
    stepperValue: {
        minWidth: 44,
        height: 38,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 10,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 14,
        backgroundColor: colors.card,
    },
    stepperValueText: {
        ...fonts.bodyMedium,
        fontVariant: ["tabular-nums"],
        fontWeight: "600",
        color: colors.text900,
    },

    // ============================================
    // FOOTER ACTIONS
    // ============================================
    footerActions: {
        marginTop: 24,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 12,
    },
    primaryActions: {
        flexDirection: "row",
        gap: 12,
    },
    primaryButton: {
        backgroundColor: colors.primaryGreen,
        paddingVertical: 14,
        paddingHorizontal: 24,
        borderRadius: 10,
        ...(Platform.OS !== 'web' && {
            shadowColor: colors.primaryGreenDark,
            shadowOpacity: 0.2,
            shadowRadius: 8,
            shadowOffset: { width: 0, height: 2 },
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0px 2px 8px rgba(22,101,52,0.2)',
        }),
    },
    primaryButtonPressed: {
        backgroundColor: colors.darkGreen,
    },
    primaryButtonText: {
        ...fonts.button,
        color: "white",
        fontWeight: "600",
    },
    primaryButtonDisabled: {
        backgroundColor: colors.mediumGray,
        ...(Platform.OS !== 'web' && {
            shadowOpacity: 0,
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: 'none',
        }),
    },
    primaryButtonTextDisabled: {
        color: colors.lightGray,
    },
    primaryButtonContent: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    buttonLoader: {
        marginRight: 8,
    },
    secondaryButton: {
        paddingVertical: 14,
        paddingHorizontal: 20,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: "transparent",
    },
    secondaryButtonPressed: {
        backgroundColor: colors.bgWeak,
    },
    secondaryButtonText: {
        ...fonts.button,
        color: colors.text700,
        fontWeight: "500",
    },

    // ============================================
    // DISABLED STATES
    // ============================================
    pillDisabled: {
        backgroundColor: colors.bgWeak,
        borderColor: colors.border,
        opacity: 0.5,
    },
    pillTextDisabled: {
        color: colors.mediumGray,
    },
    stepperButtonDisabled: {
        backgroundColor: colors.bgWeak,
        borderColor: colors.border,
        opacity: 0.5,
    },
    stepperButtonTextDisabled: {
        color: colors.mediumGray,
    },
    // ============================================
    // CONFIRMATION SCREEN
    // ============================================
    confirmationContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
    },
    confirmationCard: {
        backgroundColor: colors.card,
        borderRadius: 16,
        padding: 36,
        maxWidth: 480,
        width: '100%',
        ...(Platform.OS !== 'web' && {
            shadowColor: "#000",
            shadowOpacity: 0.04,
            shadowRadius: 24,
            shadowOffset: { width: 0, height: 4 },
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0px 4px 24px rgba(0,0,0,0.04)',
        }),
        alignItems: 'center',
    },
    confirmationTitle: {
        ...fonts.h2,
        color: colors.primaryGreenDark,
        textAlign: 'center',
        marginBottom: 8,
    },
    confirmationSubtitle: {
        ...fonts.bodyMedium,
        color: colors.text600,
        textAlign: 'center',
        marginBottom: 28,
    },
    confirmationDetails: {
        width: '100%',
    },
    digestSummary: {
        backgroundColor: colors.bgWeak,
        borderRadius: 12,
        padding: 18,
        marginBottom: 28,
    },
    confirmationSummaryTitle: {
        ...fonts.bodySmall,
        fontWeight: '600',
        color: colors.text700,
        marginBottom: 12,
    },
    confirmationSummaryItem: {
        ...fonts.bodySmall,
        color: colors.text600,
        marginBottom: 4,
    },
});
